/**
 * WHICH CONTOUR A CMS SECTION IS — production or stage.
 *
 * WHY THIS FILE EXISTS. The contour is the project segment of the URL and
 * nothing in this app knew that. `/cms/aiguru/free-access` and
 * `/cms/aiguru-stage/free-access` look identical on screen, and "aiguru" reads
 * to a person as the product's name rather than as "production". On 13.10 the
 * owner edited production free access believing he was on stage, because the
 * screen said nothing and the stage section is absent from the project list.
 *
 * THE PAIR IS DECLARED BY THE SERVER, AND THIS IS A COPY. The authority is
 * backend/web/lesson/get_lesson.js in the backend repository:
 *
 *     const CMS_ROOT_PROD  = "aiguru";
 *     const CMS_ROOT_STAGE = "aiguru-stage";
 *
 * It lives in another repository and cannot be imported, so the names are
 * written again here — the same trade the server itself makes for the line
 * names it shares with Wield. What is copied is two strings; if they ever
 * disagree, this screen points an editor at a section the server does not
 * read, which is visible immediately (an empty course list), rather than
 * silently writing the wrong one.
 *
 * ONLY A DECLARED PAIR GETS A SWITCH. Every other section is listed here as
 * nothing at all, and the header then states the section id as a plain fact
 * instead of calling it "production" — which would be a claim about a contour
 * nobody measured. Measured today: aiguru is the only product with a pair.
 */

export type ContourKind = "prod" | "stage";

export type Contour = {
  /** Which half of the pair the current section is. */
  kind: ContourKind;
  /** The production section id. */
  prod: string;
  /** The stage section id. */
  stage: string;
};

const PAIRS: ReadonlyArray<{ prod: string; stage: string }> = [
  { prod: "aiguru", stage: "aiguru-stage" },
];

/**
 * The contour of a section, or null when the section has no declared twin.
 * Null means "unknown", never "production".
 */
export function contourOf(project: string | null | undefined): Contour | null {
  if (!project) return null;
  for (const p of PAIRS) {
    if (project === p.prod) return { kind: "prod", prod: p.prod, stage: p.stage };
    if (project === p.stage) return { kind: "stage", prod: p.prod, stage: p.stage };
  }
  return null;
}

/** True when writing here reaches buyers. False for stage AND for unknown. */
export function isProduction(project: string | null | undefined): boolean {
  return contourOf(project)?.kind === "prod";
}

/**
 * The same page in the other contour, with the tab kept.
 *
 * Switching contour must not cost the editor their place: `/cms/aiguru/
 * free-access` becomes `/cms/aiguru-stage/free-access`, not the project root.
 * Anything after the project segment is carried over untouched.
 */
export function pathInContour(pathname: string, to: ContourKind): string | null {
  const rest = pathname.replace(/^\/cms\/?/, "");
  const parts = rest.split("/");
  const project = parts[0];
  const c = contourOf(project);
  if (!c) return null;
  parts[0] = to === "prod" ? c.prod : c.stage;
  return `/cms/${parts.join("/")}`;
}
